import * as React from 'react';
import { useCallback, useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import LinearProgress from '@mui/material/LinearProgress';
import { makeStyles } from '@mui/styles';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useMessages } from 'modules/intl';
import ErrorPage from 'modules/common/routes/ErrorPage/ErrorPage';
import { postRequest } from 'utils/helper';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    width: theme.spacing(50)
  },
  dialogTitle: {
    color: theme.palette.text.primary,
    ...theme.typography.h6
  }
}));

const useDialogStyles = makeStyles((theme) => ({
  disconnectDialogTitle: {
    color: theme.palette.text.secondary,
    ...theme.typography.h5
  },
  disconnectDialogContext: {
    color: theme.palette.text.primary
  },
  uninstallButton: {
    color: theme.palette.primary.main
  },
  disconnectIndicator: {
    verticalAlign: 'middle'
  }
}));

type UninstallMessageDialogProps = {
  isOpen: boolean;
  isWaitDialog: boolean;
};

type UninstallDialogProps = {
  connectorName: string;
  companyKey: string;
  dialogOpen: boolean;
  handleDialogClose: () => void;
  isDisconnecting: boolean;
  name: string;
  setIsDisconnecting: React.Dispatch<React.SetStateAction<boolean>>;
};

const PROGRESS_INCREMENT = 10;
const PROGRESS_INTERVAL = 2200;
const MAX_PROGRESS = 80;
const FINAL_PROGRESS = 100;
const SHOW_WAIT_DIALOG = 15000;

const messages = [
  'connectors.uninstall',
  'connectorSetting.uninstallMessage1',
  'connectorSetting.uninstall',
  'connectorSetting.cancel'
];

export const UninstallMessageDialog: React.FC<UninstallMessageDialogProps> = ({
  isOpen,
  isWaitDialog
}: UninstallMessageDialogProps) => {
  const classes = useStyles();

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        window.location.reload();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (isWaitDialog) {
    return (
      <Dialog
        open={isWaitDialog}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        PaperProps={{ className: classes.dialogPaper }}
        data-testid="uninstallWaitText"
      >
        <DialogTitle className={classes.dialogTitle}>
          <FormattedMessage id="connectors.pleaseWaitTitle" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="wait-dialog-description">
            <FormattedMessage id="connectors.uninstallWaitMessage" />
          </DialogContentText>
        </DialogContent>
      </Dialog>
    );
  }

  if (isOpen) {
    return (
      <div data-testid="mui-uninstall-dialog">
        <Dialog
          open={isOpen}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          PaperProps={{ className: classes.dialogPaper }}
          data-testid="uninstallText"
        >
          <DialogTitle className={classes.dialogTitle}>
            <FormattedMessage id="connectors.uninstallSuccessTitle" />
          </DialogTitle>
        </Dialog>
      </div>
    );
  }
  return null;
};

export const UninstallDialog: React.FC<UninstallDialogProps> = ({
  connectorName,
  companyKey,
  dialogOpen,
  handleDialogClose,
  isDisconnecting,
  name,
  setIsDisconnecting
}: UninstallDialogProps) => {
  const classes = useDialogStyles();
  const [status, setStatus] = useState<any>();
  const [progress, setProgress] = useState<number>(0);
  const [showWaitDialog, setShowWaitDialog] = useState<boolean>(false);
  const [showErrorPage, setShowErrorPage] = useState(false);

  const detailsMessages = useMessages(messages);

  const handleUninstallClick = useCallback(async () => {
    setIsDisconnecting(true);
    const body = {
      connectorName: connectorName,
      companyKey: companyKey
    };
    const timer = setInterval(() => {
      setProgress((prevProgress) =>
        Math.min(MAX_PROGRESS, prevProgress + PROGRESS_INCREMENT)
      );
    }, PROGRESS_INTERVAL);
    const waitDialogTimer = setTimeout(() => {
      setShowWaitDialog(true);
    }, SHOW_WAIT_DIALOG);
    try {
      const res = await postRequest(
        '/integration-settings-api/uninstall-connector',
        body,
        true
      );
      setProgress(FINAL_PROGRESS);
      clearInterval(timer);
      clearTimeout(waitDialogTimer);
      // A 201 means the uninstall completed server-side. Trigger the reload on
      // the status alone so a missing/omitted `success` flag cannot strand the
      // UI on the detail page. Only an explicit `success === false` (a partial
      // failure reported by the backend) downgrades to the error path.
      if (res.status === 201 && res.data?.success !== false) {
        setShowWaitDialog(false);
        setStatus(res.status);
      } else {
        setShowErrorPage(true);
      }
    } catch (error: any) {
      if (error.response) {
        setShowErrorPage(error.response.status);
      }
      clearInterval(timer);
      clearTimeout(waitDialogTimer);
    }
  }, [setIsDisconnecting, connectorName, companyKey]);

  if (showErrorPage) {
    return <ErrorPage open={true} />;
  }

  return (
    <Dialog open={dialogOpen} data-testid="disconnecting-dialog">
      {isDisconnecting && <LinearProgress />}
      <DialogTitle
        className={classes.disconnectDialogTitle}
        data-testid="disconnecting-dialog-title"
      >
        {detailsMessages['connectors.uninstall']} {name}
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          className={classes.disconnectDialogContext}
          data-testid="disconnecting-dialog-message1"
        >
          {detailsMessages['connectorSetting.uninstallMessage1']}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleDialogClose}
          disabled={isDisconnecting}
          color="grey"
          autoFocus
          variant="contained"
          data-testid="disconnecting-dialog-cancel-button"
        >
          {detailsMessages['connectorSetting.cancel']}
        </Button>
        <Button
          onClick={handleUninstallClick}
          variant="text"
          value={status}
          className={classes.uninstallButton}
          disabled={isDisconnecting}
          data-testid="disconnecting-dialog-disconnecting-button"
        >
          {isDisconnecting ? (
            <>
              <CircularProgress
                size={22}
                variant="determinate"
                value={progress}
                data-testid="disconnect-progress"
                className={classes.disconnectIndicator}
              />
              <UninstallMessageDialog
                isOpen={status === 201}
                isWaitDialog={showWaitDialog}
              />
            </>
          ) : (
            detailsMessages['connectorSetting.uninstall']
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
